// ============================================================
// I1：仿真配置（流体/能耗参数包）
// - 参数包与阈值模板来自服务端 /simulation/presets
// - 「应用到场景」：写入 scene.config.simulation（命令总线可撤销、随工程保存）
// - 「生成告警规则」：参数 → 规则草稿（人工确认后创建，规则引擎页启用）
// ============================================================
import { useCallback, useEffect, useState } from 'react';
import { Button, Drawer, InputNumber, Modal, Segmented, Space, Tag, Tooltip, Typography, message } from 'antd';
import { ExperimentOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { commandBus } from '../../core/commandBus';
import useSceneStore from '../../stores/sceneStore';
import { createRule } from '../../api/rules';
import { fetchSimulationPresets, fetchSimulationRuleDraft, type SimulationPackView } from '../../api/simulation';

const CATEGORY_LABEL: Record<string, string> = { fluid: '流体参数', energy: '能耗参数' };

export default function SimulationButton() {
  const [open, setOpen] = useState(false);
  const [packs, setPacks] = useState<SimulationPackView[]>([]);
  const [category, setCategory] = useState<'fluid' | 'energy'>('fluid');
  const [thresholds, setThresholds] = useState<Record<string, { warn?: number; critical?: number }>>({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const applied = useSceneStore((s) => s.simulation);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchSimulationPresets();
      setPacks(r.packs ?? []);
      const init: Record<string, { warn?: number; critical?: number }> = {};
      for (const p of r.packs ?? []) {
        for (const param of p.params) {
          init[param.key] = { ...(param.warn !== undefined ? { warn: param.warn } : {}), ...(param.critical !== undefined ? { critical: param.critical } : {}) };
        }
      }
      // 已应用到场景的阈值优先
      const cur = useSceneStore.getState().simulation;
      if (cur?.params) {
        for (const [k, v] of Object.entries(cur.params)) init[k] = { ...(init[k] ?? {}), ...v };
      }
      setThresholds(init);
    } catch {
      message.error('仿真参数包加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const currentPack = packs.find((p) => p.category === category);

  const applyToScene = async () => {
    if (!currentPack) return;
    const params: Record<string, { warn?: number; critical?: number }> = {};
    for (const p of currentPack.params) {
      const t = thresholds[p.key];
      if (t) params[p.key] = { ...(t.warn !== undefined ? { warn: t.warn } : {}), ...(t.critical !== undefined ? { critical: t.critical } : {}) };
    }
    setBusy(true);
    const r = await commandBus.applyBatch(
      [{ type: 'scene.simulation.set', payload: { simulation: { packs: [currentPack.key], params } } }],
      { label: `simulation:${currentPack.key}` },
    );
    setBusy(false);
    if (!r.ok) {
      message.error(r.error ?? '应用失败');
      return;
    }
    message.success(`已应用「${currentPack.name}」到场景（可撤销，随保存持久化）`);
  };

  const generateRule = async (pack: SimulationPackView, paramKey: string) => {
    const t = thresholds[paramKey] ?? {};
    const value = t.critical ?? t.warn;
    if (value === undefined) {
      message.warning('请先填写告警阈值（warn 或 critical）');
      return;
    }
    setBusy(true);
    try {
      const draft = await fetchSimulationRuleDraft({ packKey: pack.key, paramKey, value });
      setBusy(false);
      Modal.confirm({
        title: `生成告警规则：${draft.name}`,
        width: 520,
        content: (
          <div style={{ fontSize: 12 }}>
            <div style={{ marginBottom: 6 }}>{draft.description}</div>
            <pre style={{ background: '#0f1420', color: '#9cf', padding: 8, borderRadius: 6, whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto' }}>
              {JSON.stringify({ conditions: draft.conditions, actions: draft.actions }, null, 2)}
            </pre>
            <Typography.Text type="secondary">创建后默认未启用，请在「规则引擎」页核对并启用。</Typography.Text>
          </div>
        ),
        okText: '创建规则',
        onOk: async () => {
          try {
            await createRule({ ...draft, eventCodes: draft.eventCodes ?? undefined });
            message.success('规则已创建（规则引擎页可启用）');
          } catch (e) {
            message.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '创建规则失败');
            throw e;
          }
        },
      });
    } catch (e) {
      setBusy(false);
      message.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '规则草稿生成失败');
    }
  };

  return (
    <>
      <Tooltip title="仿真配置（流体/能耗参数包 → 阈值告警规则）">
        <Button size="small" icon={<ExperimentOutlined />} onClick={() => setOpen(true)} data-testid="simulation-btn">
          仿真配置
        </Button>
      </Tooltip>
      <Drawer title="仿真配置（参数包）" open={open} onClose={() => setOpen(false)} size={520} destroyOnHidden>
        <div data-testid="simulation-drawer">
          <Space orientation="vertical" style={{ width: '100%' }} size={12}>
            <Segmented
              value={category}
              onChange={(v) => setCategory(v as 'fluid' | 'energy')}
              options={[
                { label: CATEGORY_LABEL.fluid, value: 'fluid' },
                { label: CATEGORY_LABEL.energy, value: 'energy' },
              ]}
            />
            {currentPack ? (
              <>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {currentPack.description}
                  <Tooltip title="参数阈值用于生成告警规则；应用到场景后会随工程保存，供看板/绑定引用。">
                    <InfoCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />
                  </Tooltip>
                </Typography.Text>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {currentPack.params.map((p) => (
                    <div key={p.key} style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 10 }}>
                      <Space size={6} wrap style={{ marginBottom: 6 }}>
                        <span style={{ fontWeight: 600 }}>{p.name}</span>
                        <Tag>{p.key}</Tag>
                        {p.unit ? <Tag color="blue">{p.unit}</Tag> : null}
                        {p.default !== undefined ? <Typography.Text type="secondary" style={{ fontSize: 12 }}>默认 {p.default}</Typography.Text> : null}
                      </Space>
                      <Space size={8} wrap>
                        <span style={{ fontSize: 12 }}>预警</span>
                        <InputNumber
                          size="small"
                          style={{ width: 110 }}
                          value={thresholds[p.key]?.warn}
                          onChange={(v) => setThresholds((prev) => ({ ...prev, [p.key]: { ...prev[p.key], warn: v ?? undefined } }))}
                        />
                        <span style={{ fontSize: 12 }}>严重</span>
                        <InputNumber
                          size="small"
                          style={{ width: 110 }}
                          value={thresholds[p.key]?.critical}
                          onChange={(v) => setThresholds((prev) => ({ ...prev, [p.key]: { ...prev[p.key], critical: v ?? undefined } }))}
                        />
                        <Button size="small" loading={busy} data-testid={`sim-generate-${p.key}`} onClick={() => void generateRule(currentPack, p.key)}>
                          生成告警规则
                        </Button>
                      </Space>
                    </div>
                  ))}
                </div>
                <Space>
                  <Button type="primary" loading={busy || loading} onClick={() => void applyToScene()} data-testid="sim-apply">
                    应用到场景
                  </Button>
                  {applied ? (
                    <Button
                      onClick={async () => {
                        await commandBus.applyBatch([{ type: 'scene.simulation.set', payload: { simulation: null } }], { label: 'simulation:clear' });
                        message.info('已清除场景仿真参数');
                      }}
                    >
                      清除
                    </Button>
                  ) : null}
                </Space>
                {applied ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    当前场景已应用：{applied.packs.join('、')}（{Object.keys(applied.params).length} 个参数）
                  </Typography.Text>
                ) : null}
              </>
            ) : (
              <Typography.Text type="secondary">加载中…</Typography.Text>
            )}
          </Space>
        </div>
      </Drawer>
    </>
  );
}
